import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { promoteAzioneToMit } from '../../lib/api';

/**
 * Pulsante "Promuovi azione → MIT".
 * Mostra un piccolo popover con date picker (default = oggi).
 *
 * Props:
 *  - azioneId: id dell'azione da promuovere
 *  - onPromoted(payload): callback dopo promozione riuscita (riceve `{ mit, deleted_azione_id }`)
 *  - testid: data-testid del pulsante
 */
export default function PromoteToMitButton({ azioneId, onPromoted, testid }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await promoteAzioneToMit(azioneId, date);
      toast.success(`Promossa a MIT per il ${date}`);
      setOpen(false);
      if (onPromoted) onPromoted(res);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore promozione';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <span ref={rootRef} className="relative inline-flex shrink-0">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Promuovi a MIT"
        className="p-1 text-mute hover:text-accent"
        data-testid={testid || `promote-mit-btn-${azioneId}`}
      >
        <ArrowUpRight size={14} strokeWidth={1.5} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 z-50 bg-canvas border border-ink p-3 shadow-lg flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
          data-testid={`promote-mit-popover-${azioneId}`}
        >
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-soft shrink-0">MIT per il</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-2 py-1 border border-line focus:border-ink outline-none font-mono text-xs"
            data-testid={`promote-mit-date-${azioneId}`}
          />
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="px-3 py-1 bg-ink text-canvas font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-accent disabled:opacity-50"
            data-testid={`promote-mit-confirm-${azioneId}`}
          >
            {busy ? '…' : 'Promuovi'}
          </button>
        </div>
      )}
    </span>
  );
}
